import type { FmeaStandard } from "../types";

/**
 * Get the appropriate AI prompt for FMEA generation based on the standard
 */
export const getFmeaGenerationPrompt = (
    standard: FmeaStandard,
    pDiagram: any,
    customerRequirements: any[],
    availableFunctions: string[],
    existingFmea: any[],
    globalKnowledge: any[],
    existingControls: any[],
    existingDvpr: any[],
    maxItems: number
): string => {

    const baseContext = `
P-Diagram for Function: ${pDiagram.consolidatedFunctionId}
${JSON.stringify(pDiagram, null, 2)}

Customer Requirements (Voice of Customer):
${JSON.stringify(customerRequirements.map(r => ({ id: r.id, text: r.requirementText, metric: r.metric, target: r.target })), null, 2)}

Available Top-Level Functions: ${JSON.stringify(availableFunctions)}

Existing Failure Modes for this function: ${JSON.stringify(existingFmea.map(f => f.failureMode))}

HISTORICAL KNOWLEDGE (Global FMEA Database - Cross-Project Learning):
${JSON.stringify(globalKnowledge.slice(0, 50).map(k => ({ failureMode: k.failureMode, effects: k.effects, RPN: k.RPN })), null, 2)}

Existing Controls: ${JSON.stringify(existingControls.map(c => ({ id: c.id, name: c.name, type: c.type })), null, 2)}

Existing DVP&R Tests: ${JSON.stringify(existingDvpr.map(d => ({ testId: d.testId, testName: d.testName })), null, 2)}

LIMITATION: Generate a maximum of ${maxItems} new items.
  `;

    switch (standard) {
        case 'ISO_14971':
            return `
You are an expert Medical Device Risk Management specialist trained in ISO 14971:2020.

${baseContext}

Generate new FMEA items following ISO 14971 Risk Management Methodology:

INSTRUCTIONS:
1. **Identify HAZARDS** (not just failure modes) - What could reasonably foresee going wrong?
2. **Identify HAZARDOUS SITUATIONS** - Circumstances that could lead to harm to patients/users
3. **Consider FORESEEABLE MISUSE** - How might clinicians or patients misuse the device?
4. **Estimate HARM** - Specific physical injury or damage to health (not just generic "patient harm")
5. **Calculate Risk using TWO-STAGE PROBABILITY**:
   - **P1** (1-5): Probability of hazardous situation occurring
   - **P2** (1-5): Probability of harm given the hazardous situation exists
   - **S** (1-5): Severity of harm
   - **Risk Estimate** = P1 × P2 × S

6. **Determine Risk Acceptability**:
   - **Acceptable** (Risk < 10): No further action required
   - **ALARP** (Risk 10-50): As Low As Reasonably Practicable - justify benefit-risk
   - **Unacceptable** (Risk > 50): MUST be reduced before release

7. **Propose RISK CONTROL MEASURES** (in order of priority):
   a) Inherent safety by design (eliminate hazard)
   b) Protective measures (guards, alarms, fail-safes)
   c) Information for safety (warnings, training, labeling)

8. **Estimate RESIDUAL RISK** after controls are applied

COMPLIANCE REQUIREMENTS:
- Reference FDA 510(k) or PMA requirements where applicable
- Consider benefit-risk analysis for Class II/III devices
- Link to IEC 60601 (electrical safety) or ISO 13485 (QMS) if relevant
- Document traceability to Design Controls (21 CFR 820.30)

CROSS-PROJECT LEARNING:
Review "HISTORICAL KNOWLEDGE" for similar medical devices. If a hazard from a past project is relevant, INCLUDE IT.

Ignore hazards semantically identical to existing ones.

Populate 'notes' with:
- Rationale for P1, P2, S estimates
- Reference to P-Diagram noise factors
- Link to customer requirements
- Mention if derived from Historical Knowledge
- Suggestions for new DVP&R tests (e.g., clinical trials, usability studies)
`;

        case 'MIL_STD_1629A':
            return `
You are an expert Aerospace Reliability Engineer trained in MIL-STD-1629A (FMECA).

${baseContext}

Generate new FMECA items following MIL-STD-1629A Methodology:

INSTRUCTIONS:
1. **Identify FAILURE MODES** for each hardware item/function
2. **Determine FAILURE EFFECTS** at three levels:
   - Local Effect (component level)
   - Next Higher Level (subsystem)
   - End Effect (system/mission impact)

3. **Assign MISSION PHASE**:
   Examples: Launch, Ascent, Orbit, Re-entry, Landing, Ground Operations

4. **Classify SEVERITY CATEGORY**:
   - **Category I (Catastrophic)**: Loss of life, loss of vehicle/system
   - **Category II (Critical)**: Severe injury, major system damage, mission failure
   - **Category III (Marginal)**: Minor injury, minor damage, mission degradation
   - **Category IV (Negligible)**: No significant impact on personnel or mission

5. **Calculate CRITICALITY NUMBER (Cm)**:
   Formula: **Cm = λ × α × β × t**
   
   Where:
   - **λ (lambda)**: Failure rate (failures per million hours) - Use MIL-HDBK-217 database
   - **α (alpha)**: Mode ratio (0-1) - Fraction of component failures that result in this mode
   - **β (beta)**: Conditional probability (0-1) - Probability this mode leads to critical/catastrophic effect
   - **t**: Mission time (hours)

   Example: If λ = 50 failures/10⁶ hrs, α = 0.3, β = 0.8, t = 100 hrs → Cm = 1.2

6. **Propose CORRECTIVE ACTIONS**:
   - Design changes (redundancy, derating, material upgrades)
   - Operational procedures
   - Maintenance intervals

7. **Reference MIL-HDBK-217** for failure rate data where applicable

MISSION-CRITICAL FOCUS:
- Prioritize safety of flight
- Consider single-point failures
- Evaluate redundancy and fail-safe designs
- Document criticality matrix (Severity Category vs Probability)

CROSS-PROJECT LEARNING:
Review "HISTORICAL KNOWLEDGE" from previous aerospace missions. Leverage known failure modes.

Populate 'notes' with:
- Rationale for criticality number
- Reference to MIL-HDBK-217 or test data
- Suggested test methods (vibration, thermal cycling, EMI/EMC)
- Redundancy recommendations
`;

        case 'ISO_26262':
            return `
You are an expert Automotive Functional Safety Engineer trained in ISO 26262:2018.

${baseContext}

Generate new FMEA items following ISO 26262 HARA (Hazard Analysis and Risk Assessment):

INSTRUCTIONS:
1. **Identify HAZARDOUS EVENTS** for vehicle-level functions
   Format: "<Malfunction> in <Operational Situation>" 
   Example: "Unintended acceleration in highway driving"

2. **Define OPERATIONAL SITUATION**:
   Examples: Highway driving, urban traffic, parking, rural roads, adverse weather

3. **Determine Safety Integrity using 3 Parameters**:

   **S (Severity):** Impact on occupants/other road users
   - **S0**: No injuries
   - **S1**: Light/moderate injuries
   - **S2**: Severe/life-threatening injuries (survival probable)
   - **S3**: Life-threatening/fatal injuries (survival uncertain)

   **E (Exposure):** Frequency of operational situation
   - **E0**: Incredible (< 0.1% of driving time)
   - **E1**: Very low probability (< 1%)
   - **E2**: Low probability (< 10%)
   - **E3**: Medium probability (< 33%)
   - **E4**: High probability (> 33%)

   **C (Controllability):** Driver's ability to avoid harm
   - **C0**: Controllable in general
   - **C1**: Simply controllable
   - **C2**: Normally controllable
   - **C3**: Difficult to control or uncontrollable

4. **Derive ASIL (Automotive Safety Integrity Level)** using ISO 26262 Table:
   
   | S  | E  | C  | ASIL |
   |----|----|----|------|
   | S3 | E4 | C3 | **D** |
   | S3 | E3 | C3 | **C** |
   | S2 | E4 | C2 | **B** |
   | S1 | E2 | C1 | **A** |
   | S0 | Any| Any| **QM**|

   **ASIL Definitions:**
   - **ASIL D**: Highest integrity (DC ≥ 99%, HFT = 2, extensive validation)
   - **ASIL C**: High integrity (DC ≥ 97%, HFT = 1)
   - **ASIL B**: Medium integrity (DC ≥ 90%)
   - **ASIL A**: Low integrity (DC ≥ 60%)
   - **QM**: Quality Management (no functional safety requirement)

5. **Define SAFETY GOAL**:
   Top-level requirement to prevent/mitigate the hazardous event
   Example: "Prevent unintended acceleration > 0.2 m/s²"

6. **Derive FUNCTIONAL SAFETY REQUIREMENTS** from the safety goal

7. **Specify DIAGNOSTIC COVERAGE (DC %)** and **HARDWARE FAULT TOLERANCE (HFT)**
   - ASIL D: DC ≥ 99%, HFT = 2
   - ASIL C: DC ≥ 97%, HFT = 1
   - ASIL B: DC ≥ 90%, HFT = 0 (with DC) or 1
   - ASIL A: DC ≥ 60%, HFT = 0

COMPLIANCE:
- Consider ASIL decomposition (e.g., ASIL D = ASIL B(D) + ASIL B(D))
- Reference ISO 26262 Part 3 (Concept Phase), Part 4 (Product Dev), Part 5 (Hardware), Part 6 (Software)
- Link to safety architecture (E/E system, sensors, actuators)

CROSS-PROJECT LEARNING:
Leverage "HISTORICAL KNOWLEDGE" from other automotive safety projects.

Populate 'notes' with:
- Rationale for S, E, C ratings
- ASIL justification
- Safety mechanisms (redundancy, plausibility checks, limp-home mode)
- Test recommendations (fault injection, HIL testing)
`;

        case 'IEC_61508':
            return `
You are an expert Industrial Functional Safety Engineer trained in IEC 61508.

${baseContext}

Generate new FMEA items following IEC 61508 SIL Determination:

INSTRUCTIONS:
1. **Identify HAZARDOUS EVENTS** for industrial safety functions
   Examples: Overpressure in reactor, emergency stop failure, toxic gas release

2. **Determine SIL using Risk Graph Method**:

   **C (Consequence of Hazardous Event):**
   - **C1**: Minor injury to one or more persons
   - **C2**: Serious permanent injury to one or more persons
   - **C3**: Death of one person
   - **C4**: Death of several people

   **F (Frequency of Exposure to Hazard):**
   - **F1**: Rare to more often exposure
   - **F2**: Frequent to permanent exposure

   **P (Possibility of Avoiding Hazard):**
   - **P1**: Possible under certain conditions
   - **P2**: Almost impossible

   **W (Probability of Unwanted Occurrence):**
   - Already factored into risk graph

3. **Derive SIL Level**:

   Use IEC 61508 Risk Graph to map (C, F, P) → SIL

   **SIL Requirements:**
   - **SIL 4**: PFD < 10⁻⁵ to 10⁻⁴, SFF ≥ 99%, HFT ≥ 1
   - **SIL 3**: PFD < 10⁻⁴ to 10⁻³, SFF ≥ 97%, HFT ≥ 1
   - **SIL 2**: PFD < 10⁻³ to 10⁻², SFF ≥ 90%, HFT = 0 (with diag) or 1
   - **SIL 1**: PFD < 10⁻² to 10⁻¹, SFF ≥ 60%, HFT = 0
   - **Non-SIL**: No safety integrity requirement

4. **Specify Architecture**:
   - **PFD** (Probability of Failure on Demand): Target value
   - **SFF** (Safe Failure Fraction): Percentage of safe failures
   - **HFT** (Hardware Fault Tolerance): Number of faults system can tolerate
   
   Common Architectures:
   - **1oo1**: Single channel (HFT = 0) - Requires high DC
   - **1oo2**: 1 out of 2 (one failure tolerant, HFT = 1)
   - **2oo3**: 2 out of 3 voting (HFT = 1, high availability)

5. **Define SAFETY INSTRUMENTED FUNCTIONS (SIF)**:
   Specific actions to achieve risk reduction (e.g., "Emergency Shutdown Valve closes within 2 seconds")

6. **Calculate Risk Reduction Factor (RRF)**:
   RRF = 1 / PFD
   Example: SIL 3 with PFD = 10⁻³ → RRF = 1000

COMPLIANCE:
- Reference IEC 61508 Part 2 (Hardware), Part 3 (Software), Part 6 (Application Guidelines)
- Consider systematic capability (SC) for hardware design
- Document Safety Requirements Specification (SRS)
- Specify Proof Test Interval (PTI)

CROSS-PROJECT LEARNING:
Review "HISTORICAL KNOWLEDGE" for process safety incidents.

Populate 'notes' with:
- Rationale for C, F, P classification
- Architecture justification (1oo1, 1oo2, 2oo3)
- Diagnostic coverage techniques (self-test, watchdog, comparison)
- Test recommendations (proof testing, fault injection)
`;

        case 'AIAG_VDA':
        case 'AIAG_V4':
        case 'SAE_J1739':
        default:
            return `
You are an expert Reliability Engineer trained in AIAG-VDA FMEA (2019 Handbook).

${baseContext}

Generate new FMEA items following AIAG-VDA 7-Step Approach:

INSTRUCTIONS:
1. **Planning and Preparation** (Consider project scope, team, timing)

2. **Structure Analysis**:
   - Define system hierarchy: System → Subsystem → Component
   - Create block diagram or product tree

3. **Function Analysis**:
   - Define FUNCTION: What the item is supposed to do
   - Define REQUIREMENT: Measurable specification the function must meet
   - Link to Customer Requirements

4. **Failure Analysis**:
   - Identify FAILURE MODES: Ways the function can fail to meet requirements
   - Determine FAILURE EFFECTS: Consequences on customer/system/safety

5. **Risk Analysis** - Rate using 1-10 scales:
   
   **S (Severity):** Impact on customer if failure reaches them
   - **10**: Hazardous (safety risk, violates law)
   - **9**: Very high (regulatory non-compliance)
   - **8**: High (loss of primary function, vehicle inoperable)
   - **7**: Moderate (degraded primary function)
   - **6**: Low (degraded secondary function)
   - **4-5**: Very low (fit/finish issues, noise)
   - **1-3**: None to minor

   **O (Occurrence):** Likelihood of cause happening
   - **10**: Extremely high (> 1 in 2)
   - **9**: Very high (1 in 3)
   - **7-8**: High (1 in 20 to 1 in 8)
   - **4-6**: Moderate (1 in 400 to 1 in 80)
   - **2-3**: Low (1 in 15,000 to 1 in 2,000)
   - **1**: Remote (< 1 in 1,500,000)

   **D (Detection):** Likelihood of detecting before customer
   - **10**: Absolutely no detection
   - **9**: Very remote chance
   - **7-8**: Remote chance
   - **4-6**: Moderate chance (testing, inspection)
   - **2-3**: High chance (automated testing)
   - **1**: Almost certain detection (error-proofing)

6. **Calculate Action Priority (AP)**:
   - **High (H)**: S ≥ 8 OR (S ≥ 4 AND O ≥ 7) OR (S ≥ 4 AND D ≥ 7)
   - **Medium (M)**: S = 7 OR (S ≥ 4 AND O = 5-6) OR (S ≥ 4 AND D = 5-6)
   - **Low (L)**: All others

7. **Optimization**:
   - Propose PREVENTION controls (reduce Occurrence)
   - Propose DETECTION controls (reduce Detection rating)
   - Suggest DVP&R tests to validate

COMPLIANCE:
- Ensure traceability: Requirement → Function → Failure Mode → Effect
- Reference AIAG-VDA Section 5 (Structure Analysis) and Section 6 (Function Analysis)
- Link to customer requirements (Voice of Customer)
- Consider Design FMEA vs Process FMEA scope

CROSS-PROJECT LEARNING:
Review "HISTORICAL KNOWLEDGE" database. If a failure mode from past projects is relevant, INCLUDE IT to prevent known issues.

Ignore failure modes semantically identical to existing ones.

Populate 'notes' with:
- Rationale for S/O/D ratings
- Reference to P-Diagram noise factors that cause this failure
- Link to specific customer requirement ID
- Mention if derived from Historical Knowledge
- Suggest new Prevention Controls (design robustness)
- Suggest new Detection Controls (test methods)
- Recommend DVP&R tests (e.g., ALT, HALT, DVP&R validation)
`;
    }
};
